import { useEffect } from "react";
import { signal } from "@preact/signals-react";
import { useSignals } from "@preact/signals-react/runtime";
import { useLocation } from "wouter";

import Button from "@/shared/components/Button";
import TextField from "@/shared/components/TextField";
import { buildBackendUrl } from "@/shared/api/backend";
import { applyAuthenticatedUser, sessionUser } from "@/pages/authentication/state/session";
import { homeRoute } from "@/pages/authentication/utils/auth";

import backgroundImage from "@/pages/authentication/assets/background-1.webp";
import icon from "@/pages/authentication/assets/icon.svg";
import iconLock from "@/pages/authentication/assets/icon-lock.svg";
import iconWarning from "@/pages/authentication/assets/icon-warning.svg";

type Navigate = ReturnType<typeof useLocation>[1];

type LoginPayload = {
    user?: { id?: string; name?: string; role?: string; handover_department_id?: string };
    must_change_password?: boolean;
};

type BootstrapPayload = {
    requires_password_setup?: boolean;
    admin?: { id?: string; name?: string; username?: string; role?: string };
};

type LoginViewMode = "desktop" | "mobile";

// ========================================
// Constants
// ========================================
const version = import.meta.env.VITE_APP_VERSION ?? "dev";
const build = import.meta.env.VITE_APP_BUILD ?? "dev";

// ========================================
// Properties
// ========================================
const username = signal("");
const password = signal("");
const newPassword = signal("");
const confirmNewPassword = signal("");
const errorMessage = signal<string | null>(null);
const bootstrapCheckError = signal<string | null>(null);
const submitting = signal(false);
const changingPassword = signal(false);
const requiresPasswordChange = signal(false);
const bootstrapState = signal<BootstrapPayload | null>(null);
const bootstrapLoading = signal(true);
const bootstrapCheckTimeoutMs = 8000;

// ========================================
// Actions
// ========================================
async function loadBootstrapState() {
    errorMessage.value = null;
    bootstrapCheckError.value = null;
    bootstrapState.value = null;
    bootstrapLoading.value = true;

    try {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), bootstrapCheckTimeoutMs);

        try {
            const response = await fetch(buildBackendUrl("/auth/bootstrap"), {
                method: "GET",
                credentials: "include",
                signal: controller.signal,
            });

            if (!response.ok) {
                bootstrapCheckError.value = (await readErrorPayload(response)) || "Unable to check first-time setup.";
                return;
            }

            bootstrapState.value = (await response.json()) as BootstrapPayload;
        } finally {
            window.clearTimeout(timeout);
        }
    } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
            bootstrapCheckError.value = "First-time setup check timed out. You can retry or reset it.";
        } else {
            bootstrapCheckError.value = "Unable to reach backend.";
        }
    } finally {
        bootstrapLoading.value = false;
    }
}

function validateNewPassword() {
    if (newPassword.value.trim().length < 6) {
        errorMessage.value = "New password must be at least 6 characters.";
        return false;
    }
    if (newPassword.value !== confirmNewPassword.value) {
        errorMessage.value = "New password and confirmation do not match.";
        return false;
    }

    return true;
}

async function submitLogin(event: React.SyntheticEvent<HTMLFormElement>, navigate: Navigate) {
    event.preventDefault();
    errorMessage.value = null;

    if (!username.value.trim() || !password.value) {
        errorMessage.value = "Enter a username and password.";
        return;
    }

    submitting.value = true;

    try {
        const response = await fetch(buildBackendUrl("/auth/login"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
                username: username.value.trim(),
                password: password.value,
            }),
        });

        if (!response.ok) {
            const payload = await readErrorPayload(response);
            errorMessage.value = payload || "Unable to sign in.";
            return;
        }

        const payload = (await response.json()) as LoginPayload;

        if (payload.must_change_password) {
            requiresPasswordChange.value = true;
            newPassword.value = "";
            confirmNewPassword.value = "";
            errorMessage.value = "You must change your password before proceeding.";
            return;
        }

        if (payload.user?.id && payload.user?.name && payload.user?.role) {
            applyAuthenticatedUser({ id: payload.user.id, name: payload.user.name, role: payload.user.role, handover_department_id: payload.user.handover_department_id });
        }

        navigate(homeRoute(sessionUser.value));
    } catch {
        errorMessage.value = "Unable to reach backend.";
    } finally {
        submitting.value = false;
    }
}

async function submitPasswordChange(event: React.SyntheticEvent<HTMLFormElement>, navigate: Navigate) {
    event.preventDefault();
    errorMessage.value = null;

    if (changingPassword.value) {
        return;
    }
    if (!validateNewPassword()) {
        return;
    }

    changingPassword.value = true;

    try {
        const response = await fetch(buildBackendUrl("/auth/change-password"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
                current_password: password.value,
                new_password: newPassword.value.trim(),
            }),
        });

        if (!response.ok) {
            const payload = await readErrorPayload(response);
            errorMessage.value = payload || "Failed to change password.";
            return;
        }

        const payload = (await response.json()) as LoginPayload;
        if (payload.user?.id && payload.user?.name && payload.user?.role) {
            applyAuthenticatedUser({ id: payload.user.id, name: payload.user.name, role: payload.user.role, handover_department_id: payload.user.handover_department_id });
        }

        requiresPasswordChange.value = false;
        password.value = newPassword.value.trim();
        newPassword.value = "";
        confirmNewPassword.value = "";
        navigate(homeRoute(sessionUser.value));
    } catch {
        errorMessage.value = "Unable to reach backend.";
    } finally {
        changingPassword.value = false;
    }
}

async function submitBootstrapPassword(event: React.SyntheticEvent<HTMLFormElement>, navigate: Navigate) {
    event.preventDefault();
    errorMessage.value = null;

    if (changingPassword.value) {
        return;
    }
    if (!validateNewPassword()) {
        return;
    }

    changingPassword.value = true;

    try {
        const response = await fetch(buildBackendUrl("/auth/bootstrap"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
                new_password: newPassword.value.trim(),
            }),
        });

        if (!response.ok) {
            const payload = await readErrorPayload(response);
            errorMessage.value = payload || "Failed to complete first-time setup.";
            return;
        }

        const payload = (await response.json()) as LoginPayload;
        if (payload.user?.id && payload.user?.name && payload.user?.role) {
            applyAuthenticatedUser({ id: payload.user.id, name: payload.user.name, role: payload.user.role });
        }

        bootstrapState.value = { requires_password_setup: false };
        newPassword.value = "";
        confirmNewPassword.value = "";
        navigate(homeRoute(sessionUser.value));
    } catch {
        errorMessage.value = "Unable to reach backend.";
    } finally {
        changingPassword.value = false;
    }
}

async function readErrorPayload(response: Response) {
    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
        const payload = (await response.json()) as { error?: string };
        return payload.error?.trim() ?? "";
    }

    return (await response.text()).trim();
}

// ========================================
// Components
// ========================================
function Background() {
    return (
        <div className="absolute h-full w-full overflow-hidden bg-slate-100">
            <img src={backgroundImage} alt="" className="h-full w-full object-cover opacity-20 blur-[8px]" />
            <div className="absolute inset-0 bg-white/55" />
        </div>
    );
}

function Content({ mode }: { mode: LoginViewMode }) {
    return (
        <div className="relative min-h-dvh w-full md:absolute md:h-full">
            <div className="flex min-h-dvh items-start justify-center p-3 sm:p-4 md:h-full md:items-center">
                <LoginWindow mode={mode} />
            </div>
        </div>
    );
}

function LoginWindow({ mode }: { mode: LoginViewMode }) {
    return (
        <div className={mode === "mobile" ? "flex w-full max-w-md flex-col" : "grid w-full max-w-[50rem] grid-cols-1 overflow-hidden rounded-2xl drop-shadow-md md:min-h-[40rem] md:grid-cols-10"}>
            {mode === "mobile" ? <MobileBrand /> : <LeftPanel />}
            <RightPanel mode={mode} />
        </div>
    );
}

function MobileBrand() {
    return (
        <div className="mb-4 flex justify-center">
            <img src={icon} alt="HOH - Bed Board" className="h-12 w-12" />
        </div>
    );
}

function LeftPanel() {
    return (
        <div className="flex flex-col bg-[#005FB8] p-4 text-white md:col-span-4 md:rounded-l-2xl md:p-5 md:rounded-tr-none">
            <div className="mb-5 flex flex-row items-center gap-3">
                <img src={icon} alt="Icon" className="h-8 w-8" />
                <h1 className="text-[18px] font-bold">HOH - Bed Board</h1>
            </div>

            <div className="mt-4 rounded-xl bg-white/10 p-4">
                <p className="text-sm leading-6 text-white/85">
                    Bed Board gives hospital staff a live overview of bed occupancy, patient status, isolation markers, and room context in one
                    place. Its purpose is to support faster coordination, clearer bed allocation decisions, and a shared operational view across
                    the ward.
                </p>
            </div>

            <div className="flex grow" />

            <div className="flex flex-col gap-2 pt-6">
                <div className="flex h-10 w-full items-center rounded-md bg-[#196fbf]">
                    <img className="h-full p-3" src={iconLock} alt="" />
                    <span className="text-[12px]">256-bit Encryption</span>
                </div>
                <p className="text-[11px] text-white/60">Version: {version} · Build: {build}</p>
            </div>
        </div>
    );
}

function getRightPanelSubtitle() {
    if (bootstrapLoading.value) {
        return "Checking whether first-time admin setup is required.";
    }
    if (bootstrapCheckError.value) {
        return "First-time setup check failed. You can retry or reset it.";
    }
    if (bootstrapState.value?.requires_password_setup) {
        return "Set the initial admin password before continuing.";
    }
    if (requiresPasswordChange.value) {
        return "You must change your password before continuing.";
    }

    return "Enter your credentials to access the portal.";
}

function RightPanel({ mode }: { mode: LoginViewMode }) {
    useSignals();
    const [, navigate] = useLocation();

    return (
        <div className={`flex flex-col bg-white p-5 text-black sm:p-7 md:col-span-6 md:p-12 ${mode === "mobile" ? "rounded-2xl shadow-md" : "rounded-b-2xl md:rounded-r-2xl md:rounded-bl-none"}`}>
            <h1 className="text-2xl font-bold text-[#003464] md:text-[28px]">Welcome!</h1>
            <p className="mb-4 text-slate-500">{getRightPanelSubtitle()}</p>

            <div className={mode === "mobile" ? "hidden" : "grow"} />

            {bootstrapLoading.value ? (
                <div className={`flex flex-col items-start justify-center gap-4 ${mode === "mobile" ? "py-8" : "grow"}`}>
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-[#005fb8]" />
                    <div>
                        <p className="text-sm font-medium text-slate-700">Checking first-time setup...</p>
                        <p className="mt-1 text-sm text-slate-500">Verifying whether the admin account needs an initial password.</p>
                    </div>
                    <div className="grow" />
                </div>
            ) : bootstrapCheckError.value ? (
                <div className={`flex flex-col gap-4 ${mode === "mobile" ? "py-4" : "grow"}`}>
                    <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                        {bootstrapCheckError.value}
                    </div>

                    <div className="flex gap-2">
                        <div className="flex-1">
                            <Button type="button" variant="outline" className="h-14" onClick={() => void loadBootstrapState()}>
                                Retry Check
                            </Button>
                        </div>
                    </div>

                    <div className="grow" />
                </div>
            ) : bootstrapState.value?.requires_password_setup ? (
                <form className={`flex flex-col gap-2 ${mode === "mobile" ? "py-4" : "grow"}`} onSubmit={(event) => submitBootstrapPassword(event, navigate)}>
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-700">
                        Initial admin account:{" "}
                        <span className="font-semibold">
                            {bootstrapState.value.admin?.name ?? "Administrator"} ({bootstrapState.value.admin?.username ?? "admin"})
                        </span>
                        . Set a new password to continue.
                    </div>

                    <TextField
                        type="password"
                        id="bootstrap-new-password"
                        label="New Password"
                        placeholder="New Password"
                        autoComplete="new-password"
                        value={newPassword.value}
                        onChange={(value) => newPassword.value = value}
                    />

                    <TextField
                        type="password"
                        id="bootstrap-confirm-new-password"
                        label="Confirm New Password"
                        placeholder="Confirm New Password"
                        autoComplete="new-password"
                        className="mb-6"
                        value={confirmNewPassword.value}
                        onChange={(value) => confirmNewPassword.value = value}
                    />

                    <ErrorMessage message={errorMessage.value} />

                    <div>
                        <LoginButton disabled={changingPassword.value || !newPassword.value.trim() || !confirmNewPassword.value.trim()}>
                            {changingPassword.value ? "Updating..." : "Set Admin Password"}
                        </LoginButton>
                    </div>

                    <div className={mode === "mobile" ? "hidden" : "grow"} />
                </form>
            ) : requiresPasswordChange.value ? (
                <form className={`flex flex-col gap-2 ${mode === "mobile" ? "py-4" : "grow"}`} onSubmit={(event) => submitPasswordChange(event, navigate)}>
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-700">
                        First login detected. Set a new password to continue.
                    </div>

                    <TextField
                        type="password"
                        id="new-password"
                        label="New Password"
                        placeholder="New Password"
                        autoComplete="new-password"
                        value={newPassword.value}
                        onChange={(value) => newPassword.value = value}
                    />

                    <TextField
                        type="password"
                        id="confirm-new-password"
                        label="Confirm New Password"
                        placeholder="Confirm New Password"
                        autoComplete="new-password"
                        className="mb-6"
                        value={confirmNewPassword.value}
                        onChange={(value) => confirmNewPassword.value = value}
                    />

                    <ErrorMessage message={errorMessage.value} />

                    <div>
                        <LoginButton disabled={changingPassword.value || !newPassword.value.trim() || !confirmNewPassword.value.trim()}>
                            {changingPassword.value ? "Updating..." : "Set New Password"}
                        </LoginButton>
                    </div>

                    <div className={mode === "mobile" ? "hidden" : "grow"} />
                </form>
            ) : (
                <form className={`flex flex-col gap-2 ${mode === "mobile" ? "py-4" : "grow"}`} onSubmit={(event) => submitLogin(event, navigate)}>
                    <TextField
                        id="username"
                        label="Username"
                        placeholder="Username"
                        autoComplete="username"
                        value={username.value}
                        onChange={(value) => username.value = value}
                    />

                    <TextField
                        type="password"
                        id="password"
                        label="Password"
                        placeholder="Password"
                        autoComplete="current-password"
                        className="mb-6"
                        value={password.value}
                        onChange={(value) => password.value = value}
                    />
                    <ErrorMessage message={errorMessage.value} />

                    <div>
                        <LoginButton disabled={submitting.value || !username.value.trim() || !password.value}>
                            {submitting.value ? "Signing In..." : "Sign In to Portal"} <span>&#8594;</span>
                        </LoginButton>
                    </div>

                    <div className={mode === "mobile" ? "hidden" : "grow"} />
                </form>
            )}

            {/* Security Footer */}
            <div>
                <div className="my-4 flex items-center">
                    <hr className="grow border-t border-gray-300" />
                </div>

                <div className="flex h-4 flex-row items-center justify-center">
                    <img src={iconWarning} alt="Warning Icon" className="mr-2 h-4 w-4" />
                    <h3 className="text-[12px] font-bold uppercase leading-none tracking-wider text-[#DC2626]">
                        Authorized Personnel Only
                    </h3>
                </div>

                <div className="flex flex-col pt-2">
                    <p className="text-[12px] text-slate-500">
                        Access to this system is monitored and restricted to hospital staff. Unauthorized access attempts are logged and reported.
                    </p>
                </div>
            </div>
        </div>
    );
}

function ErrorMessage({ message }: { message: string | null }) {
    if (!message) {
        return null;
    }

    return <p className="-mt-4 mb-4 text-[12px] text-[#DC2626]">{message}</p>;
}

function LoginButton({ disabled, children }: { disabled?: boolean; children: React.ReactNode }) {
    return (
        <Button
            type="submit"
            variant="primary"
            className=""
            disabled={disabled}
        >
            {children}
        </Button>
    );
}

// ========================================
// Page
// ========================================
export default function LoginView({ mode = "desktop" }: { mode?: LoginViewMode }) {
    useSignals();
	const [, navigate] = useLocation();

    useEffect(() => {
        void loadBootstrapState();
    }, []);

    if (sessionUser.value) {
	    window.queueMicrotask(() => navigate(homeRoute(sessionUser.value)));
	    return <div className="grid h-screen w-screen place-items-center bg-slate-100 text-sm font-semibold text-slate-500">Redirecting...</div>;
    }

    return (
        <div className="relative min-h-dvh w-full overflow-x-hidden md:h-screen md:w-screen md:overflow-hidden">
            <Background />
            <Content mode={mode} />
        </div>
    );
}
